import { useState } from "react";
import { Mail, Phone, MessageCircle, MapPin, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal } from "@/components/Reveal";
import { CONTACT, submitContact } from "@/lib/site";

const FAQ = [
    {
        q: "Wanneer schakel ik crisisbegeleiding in?",
        a: "Wanneer een situatie escaleert of dreigt te escaleren — bijvoorbeeld bij hevige spanningen thuis, vastgelopen zorg of gevaar voor de veiligheid van een jongere of volwassene met LVB. Neem gerust contact op; samen bekijken we wat er nodig is.",
    },
    {
        q: "Hoe snel kan Freelance T starten?",
        a: "Als zelfstandig begeleider kan ik snel schakelen, zonder lange wachtlijsten. In een acute situatie heeft bellen of appen de voorkeur.",
    },
    {
        q: "Hoe wordt crisisbegeleiding vergoed?",
        a: "De vergoeding loopt via de Jeugdwet of de Wmo, afhankelijk van de situatie. Ik werk ook rechtstreeks in opdracht van gemeenten, wijkteams en zorginstellingen. Ik denk hierin graag met u mee tijdens het intakegesprek.",
    },
];

const CHANNELS = [
    {
        icon: Mail,
        label: "E-mail",
        value: CONTACT.email,
        href: `mailto:${CONTACT.email}`,
        testid: "contact-channel-email",
    },
    {
        icon: Phone,
        label: "Telefoon",
        value: CONTACT.phoneDisplay,
        href: CONTACT.phoneHref,
        testid: "contact-channel-phone",
    },
    {
        icon: MessageCircle,
        label: "WhatsApp",
        value: "Stuur direct een bericht",
        href: CONTACT.whatsappUrl,
        testid: "contact-channel-whatsapp",
    },
];

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await submitContact(form);
            setSuccess(true);
            setForm({ name: "", email: "", message: "" });
            toast.success("Uw bericht is verzonden.");
        } catch {
            toast.error("Verzenden is niet gelukt. Probeer het opnieuw.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="contact" className="py-24 lg:py-32">
            <div className="mx-auto max-w-7xl px-5 lg:px-8">
                <Reveal>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-terracotta">
                        Contact
                    </p>
                    <h2 className="mt-4 max-w-2xl font-serif text-3xl font-semibold leading-snug tracking-tight sm:text-4xl">
                        Crisis of vraag? Ik denk direct met u mee.
                    </h2>
                </Reveal>

                <div className="mt-14 grid gap-12 lg:grid-cols-2 lg:gap-16">
                    <div>
                        <Reveal delay={0.05}>
                            <div className="space-y-4">
                                {CHANNELS.map((channel) => (
                                    <a
                                        key={channel.label}
                                        href={channel.href}
                                        target={channel.label === "WhatsApp" ? "_blank" : undefined}
                                        rel="noreferrer"
                                        data-testid={channel.testid}
                                        className="group flex items-center gap-5 rounded-2xl border border-line bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:border-terracotta/50 hover:shadow-lg"
                                    >
                                        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-moss-100 text-moss-800 transition-colors duration-300 group-hover:bg-terracotta group-hover:text-sand-50">
                                            <channel.icon size={20} />
                                        </span>
                                        <span>
                                            <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-moss-900/50">
                                                {channel.label}
                                            </span>
                                            <span className="block text-base font-semibold">
                                                {channel.value}
                                            </span>
                                        </span>
                                    </a>
                                ))}
                                <div className="flex items-center gap-5 rounded-2xl border border-dashed border-line p-5 text-moss-900/70">
                                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sand-100 text-moss-800">
                                        <MapPin size={20} />
                                    </span>
                                    <span className="text-sm font-medium">
                                        Landelijk inzetbaar — bij u thuis of op locatie
                                    </span>
                                </div>
                            </div>
                        </Reveal>

                        <Reveal delay={0.15}>
                            <h3 className="mt-12 font-serif text-2xl font-semibold">
                                Veelgestelde vragen
                            </h3>
                            <Accordion
                                type="single"
                                collapsible
                                data-testid="contact-faq-accordion"
                                className="mt-4"
                            >
                                {FAQ.map((item, i) => (
                                    <AccordionItem key={i} value={`faq-${i}`} className="border-line">
                                        <AccordionTrigger className="text-left text-base font-medium hover:text-terracotta">
                                            {item.q}
                                        </AccordionTrigger>
                                        <AccordionContent className="text-sm leading-relaxed text-moss-900/70">
                                            {item.a}
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        </Reveal>
                    </div>

                    <Reveal delay={0.1}>
                        <div className="rounded-3xl border border-line bg-white p-7 shadow-lg sm:p-10">
                            {success ? (
                                <div data-testid="contact-success-message" className="flex flex-col items-center py-16 text-center">
                                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-moss-100 text-moss-800">
                                        <CheckCircle2 size={32} />
                                    </span>
                                    <h3 className="mt-6 font-serif text-3xl font-semibold">Bericht verzonden!</h3>
                                    <p className="mt-3 max-w-sm text-sm leading-relaxed text-moss-900/70">
                                        Bedankt voor uw bericht. Ik reageer zo snel mogelijk,
                                        uiterlijk binnen 24 uur.
                                    </p>
                                    <button
                                        onClick={() => setSuccess(false)}
                                        data-testid="contact-new-message-button"
                                        className="mt-8 rounded-full border border-line px-6 py-3 text-sm font-semibold transition-colors hover:border-terracotta hover:text-terracotta"
                                    >
                                        Nieuw bericht sturen
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={onSubmit} data-testid="contact-general-form" className="space-y-5">
                                    <h3 className="font-serif text-2xl font-semibold">Stuur een bericht</h3>
                                    <div className="space-y-2">
                                        <Label htmlFor="contact-name">Uw naam</Label>
                                        <Input
                                            id="contact-name"
                                            data-testid="contact-input-name"
                                            required
                                            value={form.name}
                                            onChange={set("name")}
                                            placeholder="Uw naam"
                                            className="h-12"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="contact-email">E-mailadres</Label>
                                        <Input
                                            id="contact-email"
                                            data-testid="contact-input-email"
                                            required
                                            type="email"
                                            value={form.email}
                                            onChange={set("email")}
                                            placeholder="u@voorbeeld.nl"
                                            className="h-12"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="contact-message">Uw bericht</Label>
                                        <Textarea
                                            id="contact-message"
                                            data-testid="contact-input-message"
                                            required
                                            value={form.message}
                                            onChange={set("message")}
                                            placeholder="Waarmee kan ik u helpen?"
                                            rows={6}
                                        />
                                    </div>
                                    <Button
                                        type="submit"
                                        disabled={loading}
                                        data-testid="contact-submit-button"
                                        className="w-full rounded-full bg-terracotta py-6 text-sm font-semibold text-sand-50 transition-all duration-300 hover:bg-terracotta-dark"
                                    >
                                        {loading ? (
                                            <span className="flex items-center gap-2">
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Verzenden...
                                            </span>
                                        ) : (
                                            "Bericht versturen"
                                        )}
                                    </Button>
                                </form>
                            )}
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
